import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../shared/components/Layout';
import Modal from '../../shared/components/Modal';
import Pagination from '../../shared/components/Pagination';
import { extractErrorMessage } from '../../shared/utils/errors';
import { DepositEntry, PaginatedResponse } from '../../shared/types';
import { formatUserLabel, useUserNames } from '../../shared/hooks/useUserNames';
import { formatNetworkLabel } from '../../shared/utils/networkLabel';
import { DepositFilters, getDeposit, listDeposits } from './api';

const CURRENCY_OPTIONS = ['', 'BTC', 'ETH', 'USDT', 'TRX', 'USD'];

const SMALLEST_UNIT_DIVISOR = 100_000_000;

const formatCryptoAmount = (amount: number | string | null | undefined): string => {
  if (amount === null || amount === undefined) return '0';
  const value = typeof amount === 'string' ? Number(amount) : amount;
  if (!Number.isFinite(value)) return '0';
  const decimal = value / SMALLEST_UNIT_DIVISOR;
  return decimal.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 8,
  });
};

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

const DepositsPage: React.FC = () => {
  const [deposits, setDeposits] = useState<PaginatedResponse<DepositEntry> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [filters, setFilters] = useState<DepositFilters>({});
  const [draftFilters, setDraftFilters] = useState<DepositFilters>({});

  const [detail, setDetail] = useState<DepositEntry | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const load = useCallback(async (page: number, filtersToUse: DepositFilters) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listDeposits({ ...filtersToUse, page, per_page: 20 });
      setDeposits(data);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load deposits'));
      setDeposits(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load(currentPage, filters);
  }, [currentPage, filters, load]);

  const applyFilters = () => {
    setFilters(draftFilters);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setDraftFilters({});
    setFilters({});
    setCurrentPage(1);
  };

  const openDetail = async (deposit: DepositEntry) => {
    setDetail(deposit);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const fresh = await getDeposit(deposit.id);
      setDetail(fresh);
    } catch (err) {
      setDetailError(extractErrorMessage(err, 'Failed to load deposit'));
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetail(null);
    setDetailError(null);
  };

  const filterByUser = (userId: number) => {
    const value = String(userId);
    setDraftFilters((p) => ({ ...p, user_id: value }));
    setFilters((p) => ({ ...p, user_id: value }));
    setCurrentPage(1);
  };

  const userIds: number[] = [];
  if (deposits?.data) {
    for (const d of deposits.data) userIds.push(d.user_id);
  }
  if (detail) userIds.push(detail.user_id);
  const userNames = useUserNames(userIds);

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-white">Deposits</h1>
          <p className="text-slate-400 mt-1">
            Incoming credit transactions — who deposited, when, and how much.
          </p>
        </div>

        <div className="card p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">User ID</label>
            <input
              type="number"
              min={1}
              value={draftFilters.user_id ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, user_id: e.target.value || undefined }))
              }
              className="input py-2"
              placeholder="e.g. 42"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Currency</label>
            <select
              value={draftFilters.currency ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, currency: e.target.value || undefined }))
              }
              className="input py-2"
            >
              {CURRENCY_OPTIONS.map((c) => (
                <option key={c || 'all-cur'} value={c}>
                  {c || 'Any'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Reference</label>
            <input
              type="text"
              value={draftFilters.reference_id ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, reference_id: e.target.value || undefined }))
              }
              className="input py-2 font-mono text-xs"
              placeholder="ref id"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">From date</label>
            <input
              type="date"
              value={draftFilters.date_from ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, date_from: e.target.value || undefined }))
              }
              className="input py-2"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">To date</label>
            <input
              type="date"
              value={draftFilters.date_to ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, date_to: e.target.value || undefined }))
              }
              className="input py-2"
            />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={applyFilters} className="btn btn-primary py-2 flex-1">
              Apply
            </button>
            <button type="button" onClick={clearFilters} className="btn btn-secondary py-2">
              Clear
            </button>
          </div>
        </div>

        <div className="card overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-red-400">{error}</p>
            </div>
          ) : !deposits?.data.length ? (
            <div className="p-12 text-center text-slate-400">No deposits found.</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800/50">
                    <tr>
                      <th className="table-header">ID</th>
                      <th className="table-header">User</th>
                      <th className="table-header">Currency</th>
                      <th className="table-header text-right">Amount</th>
                      <th className="table-header text-right">Balance after</th>
                      <th className="table-header">Deposit address</th>
                      <th className="table-header">Reference</th>
                      <th className="table-header">Description</th>
                      <th className="table-header">Date</th>
                      <th className="table-header text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {deposits.data.map((deposit) => (
                      <tr key={deposit.id} className="hover:bg-slate-800/30 transition-colors">
                        <td className="table-cell font-mono text-indigo-400">#{deposit.id}</td>
                        <td className="table-cell">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); filterByUser(deposit.user_id); }}
                            className="text-sky-400 hover:text-sky-300 hover:underline transition-colors"
                            title={`Show all deposits for user #${deposit.user_id}`}
                          >
                            {formatUserLabel(deposit.user_id, userNames)}
                          </button>
                          <p className="text-xs text-slate-500 font-mono mt-0.5">#{deposit.user_id}</p>
                        </td>
                        <td className="table-cell text-white font-medium">{deposit.currency}</td>
                        <td className="table-cell text-right font-mono text-emerald-400">
                          +{formatCryptoAmount(deposit.amount)}
                        </td>
                        <td className="table-cell text-right font-mono text-slate-200">
                          {formatCryptoAmount(deposit.balance_after)}
                        </td>
                        <td className="table-cell font-mono text-xs text-slate-300 max-w-[180px] truncate" title={deposit.wallet_address ?? undefined}>
                          {deposit.wallet_address ? (
                            <>
                              {deposit.wallet_network && (
                                <span className="badge bg-slate-700/40 text-slate-300 border border-slate-600 mr-2 text-[10px]">
                                  {formatNetworkLabel(deposit.wallet_network)}
                                </span>
                              )}
                              {deposit.wallet_address}
                            </>
                          ) : (
                            <span className="text-slate-500">—</span>
                          )}
                        </td>
                        <td className="table-cell font-mono text-xs text-slate-400 max-w-[140px] truncate" title={deposit.reference_id ?? undefined}>
                          {deposit.reference_id ?? '—'}
                        </td>
                        <td className="table-cell text-slate-400 text-sm max-w-[160px] truncate" title={deposit.description ?? undefined}>
                          {deposit.description ?? '—'}
                        </td>
                        <td className="table-cell text-slate-400 text-sm whitespace-nowrap">
                          {formatDateTime(deposit.created_at)}
                        </td>
                        <td className="table-cell text-right">
                          <button
                            type="button"
                            onClick={() => openDetail(deposit)}
                            className="btn btn-secondary py-1 px-3 text-xs"
                          >
                            Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {deposits.last_page > 1 && (
                <div className="p-4 border-t border-slate-800">
                  <Pagination
                    currentPage={deposits.current_page}
                    lastPage={deposits.last_page}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Modal isOpen={detail !== null} onClose={closeDetail} title="Deposit details" size="md">
        {detailLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-10 h-10 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
          </div>
        ) : detailError ? (
          <p className="text-red-400">{detailError}</p>
        ) : detail ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-500 mb-1">Transaction ID</p>
                <p className="font-mono text-indigo-400">#{detail.id}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">User</p>
                <button
                  type="button"
                  onClick={() => { closeDetail(); filterByUser(detail.user_id); }}
                  className="text-sky-400 hover:underline"
                >
                  {formatUserLabel(detail.user_id, userNames)}
                </button>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Currency</p>
                <p className="text-white font-medium">{detail.currency}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Amount</p>
                <p className="font-mono text-emerald-400">+{formatCryptoAmount(detail.amount)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Balance after</p>
                <p className="font-mono text-slate-200">{formatCryptoAmount(detail.balance_after)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Date</p>
                <p className="text-slate-200">{formatDateTime(detail.created_at)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Network</p>
                <p className="text-slate-200">
                  {detail.wallet_network ? formatNetworkLabel(detail.wallet_network) : '—'}
                </p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-slate-500 mb-1">Deposit address</p>
                <p className="font-mono text-sm text-slate-300 break-all">{detail.wallet_address ?? '—'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-slate-500 mb-1">Reference ID</p>
                <p className="font-mono text-sm text-slate-300 break-all">{detail.reference_id ?? '—'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-slate-500 mb-1">Description</p>
                <p className="text-slate-300">{detail.description ?? '—'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-slate-500 mb-1">Idempotency key</p>
                <p className="font-mono text-xs text-slate-500 break-all">{detail.idempotency_key ?? '—'}</p>
              </div>
            </div>
          </div>
        ) : null}
      </Modal>
    </Layout>
  );
};

export default DepositsPage;
